// Temporal alignment (F-03.1) and dropped / inserted / duplicated frame detection (F-03.3) on small per-frame
// signatures. Signatures are normalised (zero mean, unit variance) so relighting or colour changes by the AI do
// not dominate; optional weights restrict the comparison to the person (D-21).

export interface Signature {
  data: Float32Array;
}

export function normalizeSignature(gray: ArrayLike<number>): Float32Array {
  const n = gray.length;
  let mean = 0;
  for (let i = 0; i < n; i++) mean += gray[i];
  mean /= n;
  let v = 0;
  for (let i = 0; i < n; i++) v += (gray[i] - mean) ** 2;
  const sd = Math.sqrt(v / n) || 1;
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) out[i] = (gray[i] - mean) / sd;
  return out;
}

export function sigDistance(a: Float32Array, b: Float32Array, w?: ArrayLike<number>): number {
  let s = 0;
  let ws = 0;
  for (let i = 0; i < a.length; i++) {
    const k = w ? w[i] : 1;
    if (!k) continue;
    s += Math.abs(a[i] - b[i]) * k;
    ws += k;
  }
  return ws ? s / ws : Infinity;
}

export interface OffsetResult {
  /** frameOffset to apply to B (B frame = A frame + offset, F-02.2). */
  offset: number;
  score: number;
  /** Best score / second-best distinct score; higher = more certain. */
  confidence: number;
  scores: { offset: number; score: number; overlap: number }[];
}

/**
 * Global offset: B frame index for A frame a with candidate offset o is a + o (one-to-one mapping).
 * Only offsets with at least minOverlap compared frames are considered.
 */
export function estimateOffset(
  a: (Float32Array | null)[],
  b: (Float32Array | null)[],
  maxOffset: number,
  weights?: (ArrayLike<number> | null)[],
  minOverlap = 8,
): OffsetResult | null {
  const scores: OffsetResult['scores'] = [];
  for (let o = -maxOffset; o <= maxOffset; o++) {
    let s = 0;
    let n = 0;
    for (let i = 0; i < a.length; i++) {
      const j = i + o;
      if (j < 0 || j >= b.length) continue;
      const sa = a[i];
      const sb = b[j];
      if (!sa || !sb) continue;
      s += sigDistance(sa, sb, weights?.[i] ?? undefined);
      n++;
    }
    if (n >= minOverlap) scores.push({ offset: o, score: s / n, overlap: n });
  }
  if (!scores.length) return null;
  const sorted = [...scores].sort((x, y) => x.score - y.score);
  const best = sorted[0];
  const second = sorted.find((s) => Math.abs(s.offset - best.offset) > 1) ?? sorted[1];
  return { offset: best.offset, score: best.score, confidence: second ? second.score / Math.max(1e-6, best.score) : Infinity, scores };
}

export interface Discontinuity {
  kind: 'drop' | 'insert' | 'duplicate';
  /** A frame where it happens. */
  aFrame: number;
  /** B frame where it happens. */
  bFrame: number;
  /** Number of frames dropped / inserted. */
  count: number;
}

/**
 * Local correspondence: for each B frame j, the A frame it shows is j + o_j (search ±radius around the global
 * offset). A step up of o means B skipped A frames (drop); a step down means B has extra frames (insert).
 * Duplicates are B frames nearly identical to the previous B frame while A moves at that point.
 */
export function detectDiscontinuities(
  a: (Float32Array | null)[],
  b: (Float32Array | null)[],
  globalShowOffset: number,
  radius = 6,
): { local: (number | null)[]; events: Discontinuity[] } {
  const local: (number | null)[] = new Array(b.length).fill(null);
  const bestDist: number[] = new Array(b.length).fill(Infinity);
  for (let j = 0; j < b.length; j++) {
    const sb = b[j];
    if (!sb) continue;
    let best = Infinity;
    let bestO: number | null = null;
    for (let o = globalShowOffset - radius; o <= globalShowOffset + radius; o++) {
      const i = j + o;
      if (i < 0 || i >= a.length || !a[i]) continue;
      const d = sigDistance(a[i]!, sb);
      if (d < best) {
        best = d;
        bestO = o;
      }
    }
    local[j] = bestO;
    bestDist[j] = best;
  }
  // Median filter (5) to suppress single-frame ambiguity.
  const med: (number | null)[] = local.map((_, j) => {
    const w: number[] = [];
    for (let k = j - 2; k <= j + 2; k++) if (k >= 0 && k < local.length && local[k] !== null) w.push(local[k]!);
    if (!w.length) return null;
    w.sort((x, y) => x - y);
    return w[w.length >> 1];
  });
  const events: Discontinuity[] = [];
  // Duplicates: B[j] ≈ B[j-1] while the A frames they should show differ.
  const moveA: number[] = [];
  for (let i = 1; i < a.length; i++) if (a[i] && a[i - 1]) moveA.push(sigDistance(a[i]!, a[i - 1]!));
  const typicalMove = moveA.length ? [...moveA].sort((x, y) => x - y)[moveA.length >> 1] : 0;
  const dupJ = new Set<number>();
  for (let j = 1; j < b.length; j++) {
    if (!b[j] || !b[j - 1] || med[j] === null) continue;
    const same = sigDistance(b[j]!, b[j - 1]!);
    const i = j + med[j]!;
    if (same < typicalMove * 0.15 && typicalMove > 0 && i >= 0 && i < a.length) {
      dupJ.add(j);
      events.push({ kind: 'duplicate', aFrame: i, bFrame: j, count: 1 });
    }
  }
  // Offset steps between stable runs (ignoring the duplicate frames themselves).
  let prev: number | null = null;
  for (let j = 0; j < b.length; j++) {
    const o = med[j];
    if (o === null || dupJ.has(j)) continue;
    if (prev !== null && o !== prev) {
      const d = o - prev;
      // A duplicate right before explains one step down (the duplicate "inserted" a frame).
      const explainedByDup = d < 0 && (dupJ.has(j - 1) || dupJ.has(j - 2)) && d === -1;
      if (!explainedByDup) events.push({ kind: d > 0 ? 'drop' : 'insert', aFrame: j + prev, bFrame: j, count: Math.abs(d) });
    }
    prev = o;
  }
  events.sort((x, y) => x.aFrame - y.aFrame || x.bFrame - y.bFrame);
  return { local: med, events };
}
